import { captureError } from '@utils/capture';
import { shopifyGraphqlIdToAdminId } from '@utils/shopifyIds';
import { addDays } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';

import { useCurrency } from '../../../hooks/useCurrency';
import { useAuth } from '../../../providers/AuthProvider';
import {
  useGetAddressesQuery,
  useGetOfferQuery,
  useListPaymentMethodsQuery,
  useMakeOfferMutation,
  useStartOfferFlowMutation,
} from '../../../types/generated';
import { StripeAddressDetails } from '../../draws/types';

export const usePreAuthOffer = (
  productVariantId?: string,
  productId?: string,
) => {
  const [makeOffer, { data: makeOfferData, loading: makeOfferLoading }] =
    useMakeOfferMutation();
  const [startOfferFlow] = useStartOfferFlowMutation();
  const { isLoggedIn } = useAuth();
  const { data: addresses, refetch: refetchAddresses } = useGetAddressesQuery();
  const defaultAddress = addresses?.addresses.find(
    (address) => address.isDefault,
  );
  const { data: paymentMethods, refetch: refetchPaymentMethods } =
    useListPaymentMethodsQuery();
  const defaultPaymentMethod = paymentMethods?.listPaymentMethods.find(
    (method) => method.default,
  );
  const {
    data: offerData,
    loading: offerLoading,
    refetch: refetchOffer,
  } = useGetOfferQuery({
    variables: {
      productId: shopifyGraphqlIdToAdminId(productId || ''),
    },
    skip: !isLoggedIn,
  });

  const data = offerData?.getOffer || makeOfferData?.makeOffer;

  const [currency] = useCurrency();
  const { t } = useTranslation('product');
  const [setupSecret, setSetupSecret] = useState<string | null>();
  const [offerId, setOfferId] = useState<string | null>();
  const [selectedAddressId, setSelectedAddressId] = useState<string>();
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] =
    useState<string>();
  const [isBillingSameAsShipping, setIsBillingSameAsShipping] = useState(true);
  const [billingAddress, setBillingAddress] =
    useState<StripeAddressDetails | null>(null);
  const [isNewPaymentMethod, setIsNewPaymentMethod] = useState(false);
  const [error, setError] = useState<string>();
  const [isConfirming, setIsConfirming] = useState(false);

  const handleMakeOffer = async (
    offer: number,
    expirationPeriod: number,
    shippingAddressId?: string,
    paymentMethodId?: string,
  ) => {
    if (!offerId) return false;
    try {
      await makeOffer({
        variables: {
          input: {
            offerId,
            offer: { amount: offer.toString(), currencyCode: currency },
            shippingAddressId,
            expiresAt: addDays(new Date(), expirationPeriod).toISOString(),
            paymentMethodId,
          },
        },
      });
      return true;
    } catch (err) {
      captureError(err as Error);
      setError(t('drawers.offer.errors.generic'));
      return false;
    }
  };

  const handleSetupPreAuthFlow = async () => {
    try {
      if (!productVariantId) return;
      const res = await startOfferFlow({
        variables: {
          productVariantId: shopifyGraphqlIdToAdminId(productVariantId),
        },
      });

      if (
        res.data?.startOfferFlow.setupSecret &&
        res.data?.startOfferFlow.offerId
      ) {
        setSetupSecret(res.data.startOfferFlow.setupSecret);
        setOfferId(res.data.startOfferFlow.offerId);
      }
    } catch (err) {
      captureError(err as Error);
      setError(t('drawers.offer.errors.generic'));
    }
  };

  const handleConfirmOffer = async () => {
    if (!productId) return false;
    // There could be a slight delay between stripe confirmation and db payable update so we retry a few times
    const maxRetries = 5;
    const baseDelay = 1000;

    const attemptConfirm = async (attempt: number) => {
      try {
        const res = await refetchOffer({
          productId: shopifyGraphqlIdToAdminId(productId),
        });
        if (!res.data?.getOffer?.confirmedAt) {
          throw new Error('Offer not confirmed');
        }
        return true;
      } catch (err) {
        if (attempt === maxRetries) {
          throw err;
        }
        await new Promise((resolve) => {
          setTimeout(resolve, baseDelay * 2 ** (attempt - 1));
        });
        return attemptConfirm(attempt + 1);
      }
    };

    try {
      setIsConfirming(true);
      await attemptConfirm(1);
      resetOfferFlow();
      return true;
    } catch (err) {
      captureError(err as Error);
      setError(t('drawers.offer.errors.generic'));
      return false;
    } finally {
      setIsConfirming(false);
    }
  };

  const handleAddressUpdate = async (addressId: string) => {
    try {
      await refetchAddresses();
      setSelectedAddressId(addressId);
      if (!data || !offerId)
        throw new Error('Missing offer or product variant id');

      await makeOffer({
        variables: {
          input: {
            offerId,
            offer: data.offer,
            shippingAddressId: addressId,
            paymentMethodId: selectedPaymentMethodId,
            expiresAt: data.expiresAt || addDays(new Date(), 30).toISOString(),
          },
        },
      });
    } catch (err) {
      captureError(err as Error);
      setError(t('drawers.offer.errors.generic'));
    }
  };

  const handlePaymentMethodUpdate = async (paymentMethodId: string) => {
    try {
      setSelectedPaymentMethodId(paymentMethodId);
      setIsNewPaymentMethod(false);
      if (!data || !offerId)
        throw new Error('Missing offer or product variant id');

      await makeOffer({
        variables: {
          input: {
            offerId,
            offer: data.offer,
            shippingAddressId: selectedAddressId,
            paymentMethodId,
            expiresAt: data.expiresAt || addDays(new Date(), 30).toISOString(),
          },
        },
      });
    } catch (err) {
      captureError(err as Error);
      setError(t('drawers.offer.errors.generic'));
    }
  };

  const resetOfferFlow = useCallback(() => {
    setSetupSecret(null);
    setOfferId(null);
    setSelectedAddressId(undefined);
    setSelectedPaymentMethodId(undefined);
    setIsBillingSameAsShipping(true);
    setBillingAddress(null);
    setIsNewPaymentMethod(false);
    setError(undefined);
  }, []);

  useEffect(() => {
    // refetch data when user logs in or out
    if (isLoggedIn) {
      refetchPaymentMethods();
      refetchAddresses();
      refetchOffer();
    }
  }, [refetchPaymentMethods, refetchAddresses, refetchOffer, isLoggedIn]);

  return {
    data,
    error,
    setError,
    handleMakeOffer,
    addresses,
    selectedAddressId: selectedAddressId ?? defaultAddress?.id,
    setSelectedAddressId,
    paymentMethods,
    selectedPaymentMethodId:
      selectedPaymentMethodId ?? defaultPaymentMethod?.providerPaymentMethodId,
    setSelectedPaymentMethodId,
    loading: makeOfferLoading || isConfirming || offerLoading,
    isBillingSameAsShipping,
    setIsBillingSameAsShipping,
    billingAddress,
    setBillingAddress,
    setupSecret,
    handleSetupPreAuthFlow,
    isNewPaymentMethod,
    setIsNewPaymentMethod,
    handleConfirmOffer,
    handleAddressUpdate,
    handlePaymentMethodUpdate,
  };
};
